/*
 * Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import {
  Box,
  Grid,
  List,
  ListItem,
  Typography,
  useTheme,
} from '@material-ui/core';
import useStyles from 'components/ChoreoSystem/Authenticate/AuthMainLayout/AuthMainLayout.styles';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import clsx from 'clsx';
import NotSupportedEngineMessage from 'modules/login/NotSupportedEngineMessage';
import { logoBlack } from 'utils/logos';

interface AuthMainLayoutProps {
  children: React.ReactNode;
  isMobile?: boolean;
  isSupportedEngine?: boolean;
}

function AuthMainLayout(props: AuthMainLayoutProps) {
  const { children, isMobile, isSupportedEngine } = props;
  const classes = useStyles();
  const theme = useTheme();
  const getDisplayContent = () => {
    const title = (
      <FormattedMessage
        id="pages.Login.IDevP.title"
        defaultMessage="Get Started with WSO2 Integration Platform {newLine}The AI iPaaS for Smarter Integrations"
        values={{ newLine: <br /> }}
      />
    );
    const description = (
      <FormattedMessage
        id="pages.Login.IDevP.description"
        defaultMessage="A platform for building and managing integrations with AI-assistance."
      />
    );
    const descriptionPoints = [
      <FormattedMessage
        id="pages.Login.IDevP.description.point1"
        defaultMessage="AI-assisted low-code and pro-code development"
      />,
      <FormattedMessage
        id="pages.Login.IDevP.description.point2"
        defaultMessage="Built-in support for HTTP, GraphQL, gRPC, WebSockets, TCP, Websub, and much more"
      />,
      <FormattedMessage
        id="pages.Login.IDevP.description.point3"
        defaultMessage="Integrate GenAI models, knowledge bases, AI Agents, and systems"
      />,
      <FormattedMessage
        id="pages.Login.IDevP.description.point4"
        defaultMessage="Seamless deployments with CI/CD and Kubernetes"
      />,
      <FormattedMessage
        id="pages.Login.IDevP.description.point5"
        defaultMessage="Comprehensive analytics and insights to optimize performance"
      />,
    ];
    return (
      <Box>
        <Box mb={2}>
          <Typography variant="h3">{title}</Typography>
        </Box>
        <Typography variant="h4">{description}</Typography>
        {descriptionPoints.length > 0 && (
          <List className={classes.descList} disablePadding>
            {descriptionPoints.map((point) => (
              <ListItem
                key={point.props.id}
                className={classes.descListItem}
                disableGutters
              >
                <Typography variant="h4">{point}</Typography>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    );
  };

  return (
    <Box className={classes.authMainLayoutContainer}>
      {!isMobile && (
        <Box className={classes.authMainLayoutDetails}>
          <Grid container className={classes.authMainLayoutDetailsCont}>
            <Grid item xs={12} className={classes.logoWrap}>
              <img
                className={classes.logoOffset}
                alt={logoBlack.alt}
                src={logoBlack.src}
                width={theme.spacing(40)}
              />
            </Grid>
            <Grid item xs={12}>
              {getDisplayContent()}
            </Grid>
            <Grid item xs={12} className={classes.authBgImg}>
              <img alt="Background" src="/images/login.svg" />
            </Grid>
          </Grid>
        </Box>
      )}

      <Box
        className={clsx({
          [classes.authMainLayoutForm]: true,
          [classes.authMainLayoutFormMobile]: isMobile,
        })}
      >
        <Box className={classes.authMainLayoutFormContent}>{children}</Box>

        {!isSupportedEngine && <NotSupportedEngineMessage />}
      </Box>
    </Box>
  );
}

export default AuthMainLayout;
